import styles from './conversations-header.module.scss';
import RefreshArrow from '@/assets/icons/refresh-ccw-01.svg?react';
import { ExportButton } from '../export-button/export-button';
import Glass from '@/assets/icons/magnifying-glass.svg?react';
import { Spin } from 'antd';
import { Button } from '@/components/button/button';
import FilterChip from '../filter-chip/filter-chip';
import FiltersDisclosure from '../filters-disclosure/filters-disclosure';
import SearchField from '../search-field/search-field';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Dropdown from '../form/dropdown/dropdown';
import { CallLogFilters, CallLogStatus } from '@/services/call-log-service/call-log-service.types';
import { useDebouncedFilterQuery } from '@/hooks/use-debounced-filter-query/use-debounced-filter-query';
import {
  AGENT_OPTIONS,
  CONVERSATION_TYPE_OPTIONS,
  LANGUAGE_OPTIONS,
  STATUS_CHIPS,
  STATUS_OPTIONS,
  StatusChipId,
} from './conversations-header.constants';
import { useUser } from '@/hooks/use-user/use-user';
import { useScreenSize } from '@/hooks/use-screen-size/use-screen-size';
import Icon from '@ant-design/icons';
import { CustomRangePicker } from '../custom-range-picker/custom-range-picker';
import { Dayjs } from 'dayjs';
import { CustomFilters } from '../custom-filters/custom-filters';

interface ConversationsHeaderProps {
  companiesList: string[];
  trendingTopics: string[];
  /** Every topic, for the select. `trendingTopics` is the sliced chip row. */
  allTopics?: string[];
  onRefreshData: () => void;
  onExportHistory: () => void;
  filters: CallLogFilters;
  onChangeFilters: (filters: CallLogFilters) => void;
  dates: [Dayjs | null, Dayjs | null] | null;
  setDates: (dates: [Dayjs | null, Dayjs | null] | null) => void;
  onDateRangeClear: () => void;
  onChangeDateRangeFilter: (filterValue: {
    time_range: { start_time: string; end_time: string };
  }) => void;
  isTrendingTopicsLoading?: boolean;
  searchResults?: string[];
  onSearchQueryChange: (query: string) => void;
  isSearchLoading?: boolean;
  searchError?: Error | null;
}

export const ConversationsHeader = ({
  trendingTopics,
  allTopics = [],
  onRefreshData,
  onExportHistory,
  filters,
  onChangeFilters,
  companiesList,
  dates,
  setDates,
  onDateRangeClear,
  onChangeDateRangeFilter,
  isTrendingTopicsLoading,
  searchResults,
  onSearchQueryChange,
  isSearchLoading,
  searchError,
}: ConversationsHeaderProps) => {
  const { t } = useTranslation(['conversations', 'common']);
  const { filterValue, onQueryChange } = useDebouncedFilterQuery(filters, onChangeFilters, {
    topic: undefined,
  });
  const { isFetched } = useUser();

  const screenSize = useScreenSize();

  const isMobile = screenSize.width < 768;

  const [activeTopic, setActiveTopic] = useState<string | null>(null);

  // The shell's range switch clears `topic` from outside this component, so the chip
  // highlight has to follow the filter rather than only its own click handlers.
  useEffect(() => {
    if (!filters.topic) setActiveTopic(null);
  }, [filters.topic]);

  const chipLabels: Record<StatusChipId, string> = {
    all: t('common:states.all'),
    resolved: t('conversations:status.resolved'),
    clerk: t('conversations:toolbar.clerkChip'),
  };

  const toOptions = (options: typeof STATUS_OPTIONS) =>
    options.map(({ value, labelKey }) => ({ value, label: t(`conversations:${labelKey}`) }));

  // The mockup's topic select. Chips stay: an existing feature the mockup lacks. The chips
  // show the top five, this lists every topic, so the two are not redundant.
  const topicOptions = (allTopics.length ? allTopics : trendingTopics).map((title) => ({
    value: title,
    label: title,
  }));

  // The chip row writes these two keys, so a value a chip can reach is visible, not hidden.
  const chipOwnsSelection = STATUS_CHIPS.some(
    ({ status, agent }) => filters.status === status && filters.agent === agent
  );

  // Only what the panel alone can set. `topic` has a visible select and `company_name` is
  // always set, so counting either would double-report or never read zero.
  const activeCount =
    (filters.start_time || filters.end_time ? 1 : 0) +
    (filters.type ? 1 : 0) +
    (!chipOwnsSelection && filters.agent !== undefined ? 1 : 0) +
    (!chipOwnsSelection && filters.status ? 1 : 0) +
    (filters.language ? 1 : 0);

  const handleTopicChange = (value: string | undefined) => {
    onChangeFilters({ ...filters, topic: value });
    setActiveTopic(value ?? '');
  };

  const handleConversationTypeChange = (value: string) => {
    const updatedFilters = { ...filters, type: value, topic: undefined };
    if (value !== 'AnalyticsCall') {
      updatedFilters.agent = undefined;
    }
    onChangeFilters(updatedFilters);
    setActiveTopic('');
  };

  // The chips own both keys together, so picking one clears the other. Leaving `agent`
  // set while switching to Resolved would silently intersect the two.
  const handleChipChange = (status?: CallLogStatus, agent?: boolean) => {
    onChangeFilters({ ...filters, status, agent, topic: undefined });
    setActiveTopic('');
  };

  const handleStatusChange = (value: string | undefined) => {
    onChangeFilters({
      ...filters,
      status: value as CallLogStatus | undefined,
      topic: undefined,
    });
    setActiveTopic('');
  };

  const handleAgentChange = (value: string | undefined) => {
    if (value === undefined) {
      onChangeFilters({ ...filters, agent: undefined, topic: undefined });
    } else {
      onChangeFilters({ ...filters, agent: value === 'human', topic: undefined });
    }
    setActiveTopic('');
  };

  const handleLanguageChange = (value: string | undefined) => {
    onChangeFilters({ ...filters, language: value, topic: undefined });
    setActiveTopic('');
  };

  return (
    <div className={styles.toolbar}>
      <div className={styles.statusChips}>
        {STATUS_CHIPS.map(({ id, status, agent }) => (
          <FilterChip
            key={id}
            label={chipLabels[id]}
            active={filters.status === status && filters.agent === agent}
            onClick={() => handleChipChange(status, agent)}
          />
        ))}
      </div>

      {isFetched && (
        <CustomFilters
          customClassName={styles.companySelect}
          companiesList={companiesList}
          filters={filters}
          defaultCompanyValue={undefined}
          onChangeFilters={(updatedFilters) => {
            onChangeFilters({ ...filters, ...updatedFilters, topic: undefined });
            setActiveTopic('');
          }}
          onSearchQueryChange={onSearchQueryChange}
          searchResults={searchResults}
          isSearchLoading={isSearchLoading}
          searchError={searchError}
        />
      )}

      <Dropdown
        aria-label={t('conversations:toolbar.aria.topic')}
        wrapperClassName={styles.control}
        className={styles.dropdown}
        onChange={handleTopicChange}
        placeholder={t('conversations:toolbar.allTopics')}
        options={topicOptions}
        value={filters.topic || undefined}
      />

      <SearchField
        value={filterValue}
        onChange={onQueryChange}
        placeholder={t('conversations:toolbar.searchPlaceholder')}
      />

      <ExportButton
        onPrepareExport={onExportHistory}
        isMobile={isMobile}
        label={t('conversations:toolbar.exportCsv')}
        tooltip={t('conversations:toolbar.exportTooltip')}
      />

      <Button
        aria-label={t('conversations:toolbar.refresh')}
        buttonVariant="secondary"
        onClick={onRefreshData}
        icon={<RefreshArrow stroke={styles.iconColor} />}
      />

      <FiltersDisclosure activeCount={activeCount}>
        <CustomRangePicker
          dates={dates}
          setDates={setDates}
          onClear={onDateRangeClear}
          format="YYYY-MM-DD"
          onChange={({ time_range }) => {
            onChangeDateRangeFilter({ time_range });
            onChangeFilters({
              ...filters,
              start_time: time_range.start_time,
              end_time: time_range.end_time,
              time_range: undefined,
              topic: undefined,
            });
            setActiveTopic('');
          }}
          className={
            filters.start_time !== undefined && filters.end_time !== undefined
              ? styles.activeDatePicker
              : ''
          }
        />

        <Dropdown
          aria-label={t('conversations:toolbar.aria.type')}
          className={styles.dropdown}
          wrapperClassName={styles.control}
          dropdownListClass={styles.typeDropdownList}
          onChange={handleConversationTypeChange}
          placeholder={t('conversations:toolbar.placeholders.type')}
          options={toOptions(CONVERSATION_TYPE_OPTIONS)}
          value={filters.type}
        />

        {filters.type === 'AnalyticsCall' && (
          <Dropdown
            aria-label={t('conversations:toolbar.aria.agent')}
            wrapperClassName={styles.control}
            className={styles.dropdown}
            dropdownListClass={styles.agentDropdownList}
            onChange={handleAgentChange}
            placeholder={t('conversations:toolbar.placeholders.agent')}
            options={toOptions(AGENT_OPTIONS)}
            value={filters.agent !== undefined ? (filters.agent ? 'human' : 'ai') : undefined}
          />
        )}

        <Dropdown
          aria-label={t('conversations:toolbar.aria.status')}
          wrapperClassName={styles.control}
          className={styles.dropdown}
          dropdownListClass={styles.statusDropdownList}
          onChange={handleStatusChange}
          placeholder={t('conversations:toolbar.placeholders.status')}
          options={toOptions(STATUS_OPTIONS)}
          value={filters.status}
        />

        <Dropdown
          aria-label={t('conversations:toolbar.aria.language')}
          wrapperClassName={styles.control}
          className={styles.dropdown}
          onChange={handleLanguageChange}
          placeholder={t('conversations:toolbar.placeholders.language')}
          options={toOptions(LANGUAGE_OPTIONS)}
          value={filters.language}
        />

        <div className={styles.trendingTopics}>
          {trendingTopics.length === 0 && !isTrendingTopicsLoading ? (
            <p className={styles.trendingTopicsEmpty}>
              <Icon component={() => <Glass width="10" height="14" className={styles.glassIcon} />} />
              {t('conversations:toolbar.noTrendingTopics')}
            </p>
          ) : (
            <>
              <div className={styles.trendingTopicsLabel}>
                <span>{t('conversations:toolbar.trendingTopics')}</span>
                {isTrendingTopicsLoading && <Spin size="small" style={{ marginLeft: 8 }} />}
              </div>

              {trendingTopics.map((title) => (
                <FilterChip
                  key={title}
                  label={title}
                  active={activeTopic === title}
                  onClick={() => {
                    if (activeTopic === title) {
                      setActiveTopic('');
                      onChangeFilters({ ...filters, topic: '' });
                    } else {
                      setActiveTopic(title);
                      onChangeFilters({ ...filters, topic: title });
                    }
                  }}
                />
              ))}
            </>
          )}
        </div>
      </FiltersDisclosure>
    </div>
  );
};
